export interface IPreview {
    title: string;
    subtitle: string;
    content: string[];
    imageUrl: string;
}

type UniqueCard = {
    title: string
    description: string;
    moveToId?: string;
    btnText?: string;
    cards: Array<{
        icon: string;
        title: string;
        description: string
        subItems?: Array<{
            icon: string;
            title: string;
        }>
    }>;
};

type FAQCard = {
    title: string;
    items: Array<{
        question: string;
        answers: string[];
    }>
    hoverColor: string;
}

type ServiceCard = {
    title: string;
    description: string;
    blocks: Array<{
        iconUrl: string;
        title: string;
        content: string[];
    }>;
}

type KeyFeatureCard = {
    title: string,
    cards: [
        {
            title: string;
            description: string[];
            btnText: string;
            imageUrl: string;
        },
        {
            title: string;
            description: string[];
            btnText: string;
            imageUrl: string;
        },
        {
            title: string;
            description: string[];
            btnText: string;
            imageUrl: string;
        },
    ]
};

export type CollaborationsSection = {
    title: string;
    mainSection: {
        title: string;
        points: Array<{
            icon: string;
            title: string;
        }>
    };
    secondarySection: Array<{
        title: string;
        points: Array<{
            icon: string;
            title: string;
        }>
    }>
}

export type TechnologyStackSection= {
    title: string;
    sections: Array<{
        title: string;
        technologies: Array<{
            name: string;
            width: number;
            height: number;
            icon: string;
        }>
    }>
}

type Feedback = {
    title: string;
    quoteSrc: string;
    comment: string;
    authorName: string;
    authorAvatar: string;
    authorSpecialist: string;
}

export interface SocialLukeState {
    preview: IPreview;
    unique: UniqueCard;
    faq: FAQCard;
    services: ServiceCard;
    keyFeatures: KeyFeatureCard;
    feedback: Feedback;
    collaborations: CollaborationsSection;
    technologyStack: TechnologyStackSection,
}

const imgBasePath = '/social-luke';
const iconsBasePath = `${imgBasePath}/icons`;

export const socialLukeState: SocialLukeState = {
    preview: {
        title: 'Social Luke',
        subtitle: 'Revolutionize your content development with democratized monetization opportunities.',
        content: [
            'The Social Luke platform aims to support and promote the creativity of global content creators and established influencers.',
            'The uniqueness of the software is its aspiration to democratize the monetization of content development through the means of paid bio links, collaboration tools, gifting, and bio link optimization.'
        ],
        imageUrl: `${imgBasePath}/preview.png`,
    },
    unique: {
        title: 'How is Social Luke unique?',
        description: 'Explore the core factors that underline the extraordinary nature of the SocialFi Content Creation Platform.',
        cards: [
            {
                icon: `${iconsBasePath}/monitization.png`,
                title: 'Monetized bio links',
                description: 'Adding advertisements to the page enables the creators to transform their link management into an additional source of income. This possibility revolutionizes the platform as it’s a brand-new opportunity to obtain revenue directly from the bio-link traffic.'
            },
            {
                icon: `${iconsBasePath}/tools.png`,
                title: 'Collaboration tools',
                description: 'Social Luke represents a supportive environment, which provides the new creators and established influencers with the mutual benefit of paid collaboration. The creators from these categories can hire and cooperate on the content or brand promotion where the popular influencer increases their exposure, and the new content developer enhances their visibility.'
            },
            {
                icon: `${iconsBasePath}/gifting.png`,
                title: 'Gifting',
                description: 'As a social network, the client disrupts the content development market by expanding the possibility of giving gifts to followers. The application integrates cryptocurrencies, making it convenient for the user to select a favored option and ‘gift’ the creator.'
            },
            {
                icon: `${iconsBasePath}/link.png`,
                title: 'Bio link optimization',
                description: 'Bio link optimization implies grouping all of the links of the user’s social media account. This option intensifies customer loyalty to the app as the fans can connect with the creator through multiple platforms, which can increase the bio link traffic.'
            }
        ]
    },
    faq: {
        title: 'How it all began: Agilie’s touch',
        items: [
            {
                question: 'Redefined platform’s direction',
                answers: [
                    'Our collaboration with Social Luke started in 2023. The client addressed us with the ready-made web application, whose primary objective was to promote advertising content. It was based on integrating influencers from social media apps like TikTok and YouTube.',
                    'Agilie played a prominent role in redefining and optimizing the Social Luke’s direction in platform development. Focusing on the shift in the original product idea, we assisted the client in transforming their vision by implementing innovative decisions and tech-savvy features, prioritizing flexibility and adaptation.',
                    'Thanks to Agilie’s expertise, the platform flourished from an advertisement-based concept to a progressive web platform that integrated interactive challenges that aligned perfectly with the crypto industry, as ensured a decent content monetization.'
                ],
            },
            {
                question: 'Design refinement',
                answers: [
                    'Agilie’s professional dedication to the project was not limited to the development process. We nurtured Social Luke’s business strategy with valuable insights into the user experience and intuitive functionality to make the web platform meet the needs of investors and influencers.',
                    'For this purpose, Agilie offered design refinement that comprised both minimalist modernity and conservative elegance, which promoted the product’s flexibility and likeability among the vast audience.',
                ],
            },
            {
                question: 'Integration with social media',
                answers: [
                    'Our major goal was to ensure the platform’s integration with social media like YouTube and TikTok. The influencers started the challenge by dancing videos for 30-90 seconds and setting the criteria for the victories. Calculating the number of views needed to determine the top-tier user was one of the crucial ideas Agilie\'s development team offered to the client, ensuring its practical implementation.',
                    'For this purpose, our developers restructured the existing approach to social media linkage. Instead of integration from the client’s side, our development team offered the client the option to refrain from linking their account for social media account authorization.',
                    'The SocialFi integration made us reconsider our approach to token usage, security maintenance, and data storage. <ul> <li>Our first strategic advantage represented the improvement of the overall scalability in token utilization for optimizing daily assessment of social media views.</li> <li>Our second advantage was the intensification of the influencers’ security. Since the system of challenges circulated over the cryptocurrency, we implemented an anti-fraud monitoring system. This security decision enabled us to enhance the protection of the influencers’ crypto outcome by disabling making a few withdrawals at once.</li> <li>When working on the SocialFi optimization, our development team initiated the collaboration with MongoDB on integrating the scale-out architecture to store structured and unstructured data. The outcome of this partnership enabled us to boost data processing via the platform, making it a precious solution for the client. </li> </ul>'
                ],
            },
        ],
        hoverColor: 'gray',
    },
    services: {
        title: 'Development challenges to overcome',
        description: 'While working on the project, our team encountered the challenges listed below, which made us apply a set of solutions to ensure the platform’s security and effectiveness in financial operations.',
        blocks: [
            {
                iconUrl: `${iconsBasePath}/shield.png`,
                title: 'Fraudulent actions',
                content: [
                    'While elaborating on the security measures for data safety, we clarified that the frauds tended to work with the outcome transaction processing. We implemented anti-fraud monitoring, which checked the transactions and blocked the ones for withdrawal for a short span. Our development team crafted this preventive measure to eliminate the frauds\' attempt to circumvent the system, disabling them to make a few withdrawals at once.',
                    'When receiving a request at the Agilie back-end, we created a record in Redis for storage. This record existed till the moment the transaction finished. If there was another request for the very same transaction, our development team checked whether there was a record of it in Redis, which was helpful in disabling fraud withdrawal.'
                ]
            },
            {
                iconUrl: `${iconsBasePath}/rocket.png`,
                title: 'Maintenance of effective financial transactions',
                content: [
                    'We used Fireblocks as an outside, third-party service to work with cryptocurrency. The key challenge to overcome was to optimize the work of the service in terms of ensuring effective user financial transactions. To overcome the issue, our development team created around 100 pull addresses per each blockchain. The further transaction principle of work was as follows.',
                    'For instance, the user wanted to purchase the content item in Throne, so they operated with one of the addresses. Another user could not utilize the same address. We set the time configuration so the client could pay up to a few hours. After the payment, we checked the transaction, analyzed the hash, and updated the successful operation, so another user could use this pull address.'
                ]
            }
        ]
    },
    keyFeatures: {
        title: 'Key features',
        cards: [
            {
                title: 'Optimized bio links',
                description: [
                    'Social Luke does not only enable the users to integrate all the links from their social media accounts but also attach the selected videos. For instance, users can pin up to six short videos from YouTube or TikTok. We intensified the links’ operability by enabling the automated play of the videos. This feature enables users to enhance the content traffic of their bio links.'
                ],
                btnText: '',
                imageUrl: `${imgBasePath}/optimized.png`
            },
            {
                title: 'Cryptocurrency Purchase & Gifts Support',
                description: [
                    'By implementing the gifting feature in this SocialFi platform, we enable the fans to support their favorite creators with cryptocurrency as a monetary gift.',
                    'We attached the side (third-party) service of purchasing the cryptocurrency from debit or credit cards to alleviate users’ ability to gift their favorite content creators.'
                ],
                btnText: '',
                imageUrl: `${imgBasePath}/cryptocurrency.png`
            },
            {
                title: 'Implemented challenge system',
                description: [
                    'The challenge system boosts influencers’ interest in Social Luke as by integrating their accounts via the links they can set the contests criteria, promote the participation among the followers, and enhance the overall bio link traffic.'
                ],
                btnText: '',
                imageUrl: `${imgBasePath}/challange.png`
            },
        ]
    },
    feedback: {
        title: 'Client’s feedback',
        quoteSrc: '/mad-id-images/icons/quotes.png',
        comment: 'Agilie was a perfect result-driven and cost-effective partner. Due to its multi-dimensional expertise, the team managed to optimize the web working process and add other relevant features that let our platform diversify on the market.',
        authorName: 'CEO of Social Luke',
        authorAvatar: `${iconsBasePath}/feedback.svg`,
        authorSpecialist: ''
    },
    collaborations: {
        title: 'Our professional collaboration',
        mainSection: {
            title: 'Agilie’s team',
            points: [
                {
                    icon: `${iconsBasePath}/accept.svg`,
                    title: '1 Front-end developer',
                },
                {
                    icon: `${iconsBasePath}/accept.svg`,
                    title: '1 Back-end developer',
                },
                {
                    icon: `${iconsBasePath}/accept.svg`,
                    title: '1 PM-PA',
                },
                {
                    icon: `${iconsBasePath}/accept.svg`,
                    title: '1 QA specialist',
                },
                {
                    icon: `${iconsBasePath}/accept.svg`,
                    title: '1 Designer',
                }
            ],
        },
        secondarySection: [
            {
                title: 'Client’s team',
                points: [
                    {
                        icon: `${iconsBasePath}/accept.svg`,
                        title: '3 Leaders',
                    },
                ],
            },

            {
                title: 'Cooperation time',
                points: [
                    {
                        icon: `${iconsBasePath}/accept.svg`,
                        title: '1 year',
                    },
                ],
            },
        ]
    },
    technologyStack: {
        title: 'Technology stack used',
        sections: [
            {
                title: 'Back-End',
                technologies: [
                    {
                        name: 'AWS',
                        width: 56,
                        height: 40,
                        icon: '/social-luke/technologies/aws.svg'
                    },
                    {
                        name: 'Redis',
                        width: 40,
                        height: 40,
                        icon: '/social-luke/technologies/redis.svg'
                    },
                    {
                        name: 'NestJS',
                        width: 40,
                        height: 40,
                        icon: '/social-luke/technologies/nestjs.svg'
                    },
                    {
                        name: 'TypeScript',
                        width: 40,
                        height: 40,
                        icon: '/social-luke/technologies/typescript.svg'
                    },
                ]
            },
            {
                title: 'Front-End',
                technologies: [
                    {
                        name: 'ReactJs',
                        width: 38,
                        height: 40,
                        icon: '/social-luke/technologies/reactjs.svg'
                    },
                    {
                        name: 'TypeScript',
                        width: 40,
                        height: 40,
                        icon: '/social-luke/technologies/typescript.svg'
                    },
                ]
            },
        ],
    },
}
